import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  HourglassOutlined,
  Loading3QuartersOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Button, List, Typography } from "antd";
import { useCurrentApplication } from "../../../Context/CurrentApplicationContext/CurrentApplicationContext";
import { InterviewSession } from "../../../Entities/interview-session.entity";
import dayjs from "dayjs";
import { getLastPendingRoundInfo } from "../../../Utils/job-application.utils";

const groupByRound = (interviewSessions: InterviewSession[]) => {
  return interviewSessions.reduce<Record<string, InterviewSession[]>>(
    (acc, session) => {
      const roundId = session.interviewRoundId;

      if (!acc[roundId]) {
        acc[roundId] = [];
      }

      acc[roundId].push(session);

      return acc;
    },
    {},
  );
};

const AssessmentList: React.FC = () => {
  const navigate = useNavigate();
  const { jobPost, rounds, interviewSessions, isInitDone, handleViewChange } =
    useCurrentApplication();

  const handleBack = () => {
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (isInitDone && interviewSessions.length === 0) {
      handleViewChange("apply");
    }
  }, [isInitDone, interviewSessions]);

  if (!jobPost || rounds.length === 0 || !isInitDone) {
    return null;
  }

  const grouped = groupByRound(interviewSessions);

  Object.keys(grouped).forEach((roundId) => {
    grouped[roundId].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  });

  const handleViewClick = (sessionId: string, roundId: string) => {
    const round = rounds.find((r) => r.round._id === roundId);
    if (round?.round.answerType === "simulation") {
      navigate(`/${jobPost.slug}/simulation?sessionId=${sessionId}`);
    } else {
      navigate(`/${jobPost.slug}/interview-round?sessionId=${sessionId}`);
    }
  };

  const renderAssessmentExpiry = () => {
    if (interviewSessions.length === 0) {
      return null;
    }
    const { sessionId, roundName } = getLastPendingRoundInfo(
      interviewSessions,
      rounds,
    );

    if (!sessionId || roundName) {
      return null;
    }

    const currentSession = interviewSessions.find((s) => s._id === sessionId);

    if (!currentSession || !currentSession.sessionExpiresAt) {
      return null;
    }
    return (
      <div
        className="job-posts-details__cta-card"
        style={{ marginBottom: "1.5rem" }}
      >
        <div className="job-posts-details__cta-icon-wrap">
          <HourglassOutlined />
        </div>
        <div className="job-posts-details__cta-text">
          <h2 className="job-posts-details__cta-heading">
            Complete Before Expiry
          </h2>
          <p className="job-posts-details__cta-desc">
            Please complete the {roundName} round before{" "}
            {dayjs(currentSession.sessionExpiresAt).format("DD MMM YYYY")} to
            continue in the hiring process.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="job-posts-details__card">
      <div className="job-posts-details__card-header">
        <div className="job-posts-details__job-title-header">
          <Button
            type="text"
            onClick={handleBack}
            icon={<ArrowLeftOutlined />}
          />
          <Typography.Title level={4} style={{ margin: 0 }}>
            Your Assessment Journey
          </Typography.Title>
        </div>
      </div>

      {renderAssessmentExpiry()}

      {Object.entries(grouped).map(([roundId, sessions]) => (
        <section className="job-posts-details__section" key={roundId}>
          <div className="job-posts-details__section-title">
            <span className="job-posts-details__section-bar" />
            <Typography.Title level={5} style={{ margin: 0 }}>
              {rounds.find((r) => r.round._id === roundId)?.round.title || "NA"}
            </Typography.Title>
          </div>

          {sessions.length === 1 ? (
            <div className="assessment-card">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  <QuestionCircleOutlined />{" "}
                  {sessions[0].noOfQuestionsGenerated} Questions
                </Typography.Text>

                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  |
                </Typography.Text>

                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  {sessions[0].status.toUpperCase()}
                </Typography.Text>

                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  |
                </Typography.Text>

                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  <ClockCircleOutlined /> {sessions[0].estimatedTime} Minutes
                </Typography.Text>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#595959",
                }}
              >
                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  <CalendarOutlined />{" "}
                  {dayjs(sessions[0].createdAt).format("DD MMM YYYY • hh:mm A")}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  |
                </Typography.Text>
                <Button
                  size="small"
                  onClick={() => handleViewClick(sessions[0]._id, roundId)}
                >
                  View
                </Button>
              </div>
            </div>
          ) : (
            <List
              className="attempt-list"
              header={<Typography.Text strong>Attempts</Typography.Text>}
              itemLayout="horizontal"
              dataSource={sessions}
              bordered={false}
              size="small"
              renderItem={(item, index) => (
                <List.Item
                  actions={[
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      <CalendarOutlined />{" "}
                      {dayjs(item.createdAt).format("DD MMM YYYY • hh:mm A")}
                    </Typography.Text>,
                    <Button
                      size="small"
                      onClick={() => handleViewClick(item._id, roundId)}
                    >
                      View
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      item.status === "qualified" ? (
                        <CheckCircleFilled style={{ color: "#52C41A" }} />
                      ) : item.status === "unqualified" ? (
                        <CloseCircleFilled style={{ color: "#FE5859" }} />
                      ) : (
                        <Loading3QuartersOutlined
                          spin
                          style={{ color: "#1790FF" }}
                        />
                      )
                    }
                    title={
                      <div>
                        <Typography.Text
                          style={{ fontSize: "14px", fontWeight: "normal" }}
                        >
                          Attempt {index + 1}
                        </Typography.Text>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: "10px" }}
                          >
                            <QuestionCircleOutlined />{" "}
                            {item.noOfQuestionsGenerated} Questions
                          </Typography.Text>

                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: "10px" }}
                          >
                            |
                          </Typography.Text>

                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: "10px" }}
                          >
                            {item.status.toUpperCase()}
                          </Typography.Text>

                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: "10px" }}
                          >
                            |
                          </Typography.Text>

                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: "10px" }}
                          >
                            <ClockCircleOutlined /> {item.estimatedTime} Minutes
                          </Typography.Text>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </section>
      ))}
    </div>
  );
};

export default AssessmentList;
